import {
  filterOperators,
  groupTypes,
  intervalOptions,
  DATE_COLUMN_KEYS,
} from '@opas-mobile/report-export';
import { buildChartTitle } from '@opas-mobile/report-export';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { customColors } from './misc';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../providers/UserProvider';
import {
  getTranslationKey,
  humanizeEnumBasic,
  keys,
} from './translator/translation_keys';
import {
  groupLookUp,
  participantModelLookup,
} from '../pages/analytics/count_analytics/report/Reports.utils';
import { useWorkspace } from '../providers/WorkspaceProvider';
import { useWorkspacePermissions } from '../providers/WorkspacePermissionsProvider';
import { useTemplates } from '../providers/TemplateProvider';
import { vehicleString } from './strings';
import { getLocalTime, getCalendarDate } from './time';
import i18n from '../i18n';
import { useParticipantMutations } from '../hooks/participant/participant_mutations';
import { useActionItemModal } from '../hooks/misc';
import { useNavigate } from 'react-router-dom';
import { assessmentStatus, getRoute, paths } from '../constants/strings';
import { useMTableColumns } from '../components/tables/MTableColumns';
import moment from 'moment';
import { useLabels } from '../providers/LabelProvider';
import { getLabelKey, incidentStatusTypes, incidentTypes } from './incidents';
import {
  observationClassificationTypes,
  observationStatus,
} from './observations';
import { auditTypes, rootCauseMap } from './audits';
import { defaultRanges } from './riskMatrix';
import { timecardStatusTypes } from '../pages/hr/Timecards';
import { expenseStatusTypes } from '../pages/hr/Expenses';

export { filterOperators, groupTypes, intervalOptions };

export const reportStatusTypes = {
  DRAFT: { title: 'Draft', color: customColors.YELLOW, key: 'DRAFT' },
  PUBLISHED: {
    title: 'Published',
    color: customColors.BLUE,
    key: 'PUBLISHED',
  },
  PAUSED: {
    title: 'Paused',
    color: customColors.SECONDARY,
    key: 'PAUSED',
  },
  COMPLETE: {
    title: 'Complete',
    color: customColors.GREEN,
    KEY: 'COMPLETE',
  },
};

export function ReportModels({ labels: allLabels = [] }) {
  const { auditsAndCorrectiveActions } = useFlags();
  const { enableTimecards, enableExpenses } = useFlags();
  const { t } = useTranslation();
  const {
    incidentName,
    assessmentName,
    observationName,
    assessmentTypes,
    company,
  } = useCurrentUser();
  const customIncidentTypes = company?.incidentTypes;
  const { allTemplates } = useTemplates();

  /*
  this is because the active templates from the provider pre filters templates by workspace
  if it is a workspace route and by users last workspace if not workspace route
  so this makes sure all templates are available for the custom reports regardless of the workspace
  */
  const activeTemplates = useMemo(
    () => allTemplates?.filter((t) => !t?.isArchived),
    [allTemplates],
  );

  const {
    assigneeId,
    inspectionType,
    auditType,
    type,
    subtype,
    status,
    isDraft,
    lostTime,
    nearMiss,
    location,
    vehicleId,
    dateTime,
    passed,
    priority,
    participants,
    workspaceId,
    creatorId,
    isPrivate,
    dueDate,
    TRIF,
    labels,
    riskLevel,
    templateId,
    dateOfIncident,
  } = groupTypes;

  const { openActionItemModal } = useActionItemModal();
  const navigate = useNavigate();
  const c = useMTableColumns();
  const { workspaceId: providerWorkspaceId, allVehicles } = useWorkspace();
  const { allUsers } = useWorkspacePermissions();
  const userValues = useMemo(
    () =>
      allUsers.map((u) => ({
        ...u,
        value: u.id,
        title: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
      })),
    [allUsers],
  );
  const baseTypes = [c.dateCreated, c.dateModified, c.creatorId];

  const modelOptions = useMemo(
    () => ({
      actionItem: {
        value: 'actionItems',
        label: t(keys.common.ACTION_ITEMS),
        show: true,
        icon: 'task_alt',
        group: 'workspaceId',
        columns: [
          c.actionItemType,
          c.actionItemStatus,
          c.priority,
          c.title,
          c.description,
          c.assigneeId,
          c.dueDate,
          c.workspaceId,
          c.vehicleId,
          ...baseTypes,
          c.labels,
          c.private,
        ],
        groups: [
          workspaceId,
          creatorId,
          assigneeId,
          status,
          type,
          vehicleId,
          priority,
          isPrivate,
          dueDate,
          labels,
          participants,
        ],
        onClick: ({ original }) => {
          if (original.isCorrective) {
            navigate(`/${paths.cpa}/${original.cpaId}`);
          } else if (original.type === 'INCIDENT_COMMENT') {
            navigate(
              `/${paths.incident}/${original.incident.id}?section=REVIEW`,
            );
          } else {
            openActionItemModal(original.id, null);
          }
        },
        filters: [
          {
            field: 'isCorrective',
            operator: 'eq',
            value: [`${false}`],
            default: true,
          },
          {
            field: 'type',
            operator: 'notIn',
            value: [
              'INCIDENT_COMMENT',
              'CPA-INTERIM',
              'CPA-FINAL',
              'CPA-ACCEPT',
              'CPA-REVIEW',
            ],
            default: true,
          },
        ],
        fields: [
          {
            title: keys.common.TYPE,
            value: 'type',
          },
          {
            title: keys.common.STATUS,
            value: 'status',
          },
          {
            title: keys.action_items.PRIORITY,
            value: 'priority',
          },
          {
            title: keys.common.LABELS,
            value: 'labels',
            subfilter: true,
          },
          {
            title: keys.common.CREATOR,
            value: 'creatorId',
          },
          {
            title: keys.common.ASSIGNEE,
            value: 'assigneeId',
          },
          {
            title: keys.common.PARTICIPANTS,
            value: 'participants',
            subfilter: true,
          },
        ],
        values: {
          type: [
            ...Object.keys(assessmentTypes).map((type) => ({
              value: assessmentTypes[type].key,
              title:
                t(assessmentTypes[type].title) || assessmentTypes[type]?.name,
            })),
            { value: 'GENERAL', title: 'General' },
          ],
          status: [
            {
              title: keys.common.TODO,
              value: 'TODO',
            },
            {
              title: keys.common.IN_PROGRESS,
              value: 'IN_PROGRESS',
            },
            {
              title: keys.common.COMPLETE,
              value: 'DONE',
            },
            {
              title: keys.common.DUPLICATE,
              value: 'DUPLICATE',
            },
            {
              title: keys.common.BACKLOG,
              value: 'BACKLOG',
            },
          ],
          priority: [
            {
              title: keys.action_items.LOW,
              value: 'LOW',
            },
            {
              title: keys.action_items.MEDIUM,
              value: 'MEDIUM',
            },
            {
              title: keys.action_items.HIGH,
              value: 'HIGH',
            },
          ],
          labels: allLabels?.map((l) => ({
            ...l,
            title: l?.name,
            value: l?.id,
          })),
          creatorId: userValues,
          assigneeId: userValues,
          participants: userValues,
        },
        pivotColumns: [
          ...Object.keys(assessmentTypes).map((type) => ({
            value: assessmentTypes[type].key,
            title:
              t(assessmentTypes[type].title) || assessmentTypes[type]?.name,
            fieldString: keys.common.TYPE,
            field: 'type',
            icon: assessmentTypes[type]?.icon,
            iconProps: {
              color: customColors[assessmentTypes[type]?.color.toUpperCase()],
            },
          })),
          {
            value: 'GENERAL',
            title: 'General',
            fieldString: keys.common.TYPE,
            field: 'type',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          },
          {
            title: keys.common.TODO,
            value: 'TODO',
            fieldString: keys.common.STATUS,
            field: 'status',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          },
          {
            title: keys.common.IN_PROGRESS,
            value: 'IN_PROGRESS',
            fieldString: keys.common.STATUS,
            field: 'status',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          },
          {
            title: keys.common.COMPLETE,
            value: 'DONE',
            fieldString: keys.common.STATUS,
            field: 'status',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          },
          {
            title: keys.common.DUPLICATE,
            value: 'DUPLICATE',
            fieldString: keys.common.STATUS,
            field: 'status',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          },
          {
            title: keys.common.BACKLOG,
            value: 'BACKLOG',
            fieldString: keys.common.STATUS,
            field: 'status',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          },
          {
            title: keys.action_items.LOW,
            value: 'LOW',
            fieldString: keys.action_items.PRIORITY,
            field: 'priority',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          },
          {
            title: keys.action_items.MEDIUM,
            value: 'MEDIUM',
            fieldString: keys.action_items.PRIORITY,
            field: 'priority',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          },
          {
            title: keys.action_items.HIGH,
            value: 'HIGH',
            fieldString: keys.action_items.PRIORITY,
            field: 'priority',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          },
        ],
      },
      inspection: {
        value: 'inspections',
        onClick: ({ original }) => {
          const ws =
            original?.workspaceId ||
            original?.workspace?.id ||
            providerWorkspaceId;
          const path = getRoute(
            ws,
            paths.assessment,
            original.id,
            `?type=${original?.inspectionType}`,
          );
          const url = new URL(path, window.location.origin).toString();
          window.open(url, '_blank', 'noopener,noreferrer');
        },
        label: assessmentName || t(keys.common.ASSESSMENTS),
        show: true,
        icon: 'inventory',
        group: 'workspaceId',
        columns: [
          c.inspectionType,
          c.isDraft,
          c.passed,
          c.templateTitle,
          c.subject,
          c.location,
          c.workspaceId,
          c.vehicleId,
          c.participants,
          c.labels,
          ...baseTypes,
          c.private,
        ],
        groups: [
          workspaceId,
          creatorId,
          isDraft,
          inspectionType,
          location,
          vehicleId,
          passed,
          isPrivate,
          participants,
          status,
          templateId,
        ],
        filters: [
          {
            field: 'inspectionType',
            operator: 'notIn',
            value: ['OBSERVATION', 'AUDIT'],
            default: true,
          },
        ],
        fields: [
          {
            title: keys.common.TYPE,
            value: 'inspectionType',
          },
          {
            title: keys.common.STATUS,
            value: 'status',
          },
          {
            title: keys.common.ASSET,
            value: 'vehicleId',
          },
          {
            title: keys.common.TEMPLATE,
            value: 'templateId',
          },
          {
            title: keys.common.LABELS,
            value: 'labels',
            subfilter: true,
          },
          {
            title: keys.common.CREATOR,
            value: 'creatorId',
          },
          {
            title: keys.common.PARTICIPANTS,
            value: 'participants',
            subfilter: true,
          },
        ],
        values: {
          inspectionType: [
            ...Object.keys(assessmentTypes).map((type) => ({
              value: assessmentTypes[type].key,
              title:
                t(assessmentTypes[type].title) || assessmentTypes[type]?.name,
            })),
          ],
          // Deliberately allTemplates, not activeTemplates: this list is used
          // both to populate the "pick a filter value" dropdown (via
          // ReportBuildMenu.jsx, which filters to active-only itself for
          // that specific purpose) and to resolve/display the name of an
          // already-saved filter value (createFilterString). A template
          // archived after being selected must still resolve here, or its
          // filter chip silently renders blank (t(undefined) === '') instead
          // of the template's name.
          templateId: [
            ...allTemplates.map((t) => {
              return { ...t, value: t?.id };
            }),
          ],
          vehicleId: [
            ...allVehicles.map((v) => {
              return { ...t, value: v.id, title: vehicleString(v) };
            }),
          ],
          labels: allLabels?.map((l) => ({
            ...l,
            title: l?.name,
            value: l?.id,
          })),
          creatorId: userValues,
          participants: userValues,
          status: Object.entries(assessmentStatus)
            .filter(([key]) => !key.includes('CPA') && !key.includes('SUMMARY'))
            .map(([key, value]) => ({
              value: key,
              title: value?.title,
            })),
        },
        pivotColumns: [
          ...activeTemplates.map((t) => {
            return {
              title: t?.title,
              value: t?.id,
              fieldString: keys.common.TEMPLATE,
              field: 'template_id',
              icon: assessmentTypes[t?.templateType.toLowerCase()]?.icon,
              iconProps: {
                color:
                  customColors[
                    assessmentTypes[
                      t?.templateType.toLowerCase()
                    ]?.color.toUpperCase()
                  ],
              },
            };
          }),
          ...allVehicles.map((v) => {
            return {
              value: v.id,
              title: vehicleString(v),
              fieldString: keys.common.ASSET,
              field: 'vehicle_id',
              img: v?.imageUrl,
            };
          }),
          ...Object.keys(assessmentTypes).map((type) => ({
            value: assessmentTypes[type].key,
            title:
              t(assessmentTypes[type].title) || assessmentTypes[type]?.name,
            fieldString: keys.common.TYPE,
            field: 'inspection_type',
            icon: assessmentTypes[type]?.icon,
            iconProps: {
              color: customColors[assessmentTypes[type]?.color.toUpperCase()],
            },
          })),
          ...Object.entries(assessmentStatus)
            .filter(([key]) => !key.includes('CPA') && !key.includes('SUMMARY'))
            .map(([key, value]) => ({
              value: key,
              title: value?.title,
              field: 'status',
              fieldString: keys.common.STATUS,
            })),
        ],
      },
      incident: {
        value: 'incidents',
        label: incidentName || t(keys.incidents.INCIDENTS),
        show: true,
        icon: 'fmd_bad',
        group: 'workspaceId',
        groups: [
          workspaceId,
          creatorId,
          type,
          subtype,
          status,
          lostTime,
          nearMiss,
          location,
          isPrivate,
          TRIF,
          participants,
          riskLevel,
          dateOfIncident,
        ],
        columns: [
          c.incidentStatus,
          c.risk,
          c.lostTime,
          c.nearMiss,
          c.incidentType,
          c.subtype,
          c.location,
          c.title,
          c.description,
          c.workspaceId,
          c.participants,
          c.dateOfIncident,
          ...baseTypes,
          c.private,
        ],
        onClick: (row) => {
          const path = `${paths.incident}/${row.original.id}`;
          const url = new URL(path, window.location.origin).toString();
          window.open(url, '_blank', 'noopener,noreferrer');
        },
        filters: [],
        fields: [
          {
            title: keys.common.TYPE,
            value: 'type',
          },
          {
            title: keys.common.SUBTYPE,
            value: 'subtype',
          },
          {
            title: keys.common.STATUS,
            value: 'status',
          },
          {
            title: keys.common.LABELS,
            value: 'labels',
            subfilter: true,
          },
          {
            title: keys.common.CREATOR,
            value: 'creatorId',
          },
          {
            title: keys.common.PARTICIPANTS,
            value: 'participants',
            subfilter: true,
          },
          {
            title: keys.incidents.DATE_OF_INCIDENT,
            value: 'dateOfIncident',
            isDate: true,
          },
        ],
        values: {
          type: Object.keys(customIncidentTypes || incidentTypes).map((t) => ({
            title: t,
            value: t,
          })),
          subtype: Array.from(
            new Set(
              (
                Object.values(customIncidentTypes ?? incidentTypes) ?? []
              ).flat(),
            ),
          ).map((s) => ({
            title: s,
            value: s,
          })),
          status: Object.keys(incidentStatusTypes).map((key) => ({
            title: getLabelKey(key),
            value: key,
          })),
          labels: allLabels?.map((l) => ({
            ...l,
            title: l?.name,
            value: l?.id,
          })),
          creatorId: userValues,
          participants: userValues,
        },
        pivotColumns: [
          ...Object.keys(incidentStatusTypes).map((key) => ({
            title: getLabelKey(key),
            value: key,
            fieldString: keys.common.STATUS,
            field: 'status',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          })),
          ...Object.keys(customIncidentTypes || incidentTypes).map((t) => ({
            title: t,
            value: t,
            fieldString: keys.common.TYPE,
            field: 'type',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          })),
          ...Array.from(
            new Set(
              (
                Object.values(customIncidentTypes ?? incidentTypes) ?? []
              ).flat(),
            ),
          ).map((s) => ({
            title: s,
            value: s,
            fieldString: keys.common.SUBTYPE,
            field: 'subtype',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          })),
          {
            title: keys.incidents.LOST_TIME,
            value: true,
            fieldString: keys.incidents.LOST_TIME,
            field: 'lost_time',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          },
          {
            title: keys.incidents.NEAR_MISS,
            value: true,
            fieldString: keys.incidents.NEAR_MISS,
            field: 'near_miss',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          },
          {
            title: keys.incidents.FATALITY,
            value: true,
            fieldString: keys.incidents.FATALITY,
            field: 'fatality',
            icon: 'star_outlined',
            iconProps: { color: 'secondary' },
          },
          ...Object.entries(company?.riskMatrix?.levels || defaultRanges)
            .map(([level, { min, max }]) => {
              const safeMin = Number(min);
              const safeMax = Number(max);
              if (!Number.isFinite(safeMin) || !Number.isFinite(safeMax))
                return null;

              return {
                title: level,
                value: level,
                fieldString: 'Risk Level',
                field: 'computed_risk',
                impactField: 'impact',
                probabilityField: 'probability',
                min: safeMin,
                max: safeMax,
                icon: 'warning',
                iconProps: { color: 'secondary' },
              };
            })
            .filter(Boolean),
        ],
      },
      observation: {
        value: 'observations',
        label: observationName || t(keys.observations.OBSERVATIONS),
        show: true,
        icon: 'content_paste_search',
        group: 'workspaceId',
        groups: [workspaceId, creatorId, type, status, isPrivate, participants],
        onClick: (row) => {
          const path = `${paths.observation}/${row.original.id}`;
          const url = new URL(path, window.location.origin).toString();
          window.open(url, '_blank', 'noopener,noreferrer');
        },
        columns: [
          c.observationStatus,
          c.incidentType,
          c.title,
          c.description,
          c.workspaceId,
          c.participants,
          c.private,
          ...baseTypes,
        ],
        filters: [],
        fields: [
          { title: keys.common.STATUS, value: 'status' },
          { title: keys.common.TYPE, value: 'type' },
          { title: keys.observations.POSITIVE_OBSERVATION, value: 'negative' },
          { title: keys.common.CREATOR, value: 'creatorId' },
          {
            title: keys.common.PARTICIPANTS,
            value: 'participants',
            subfilter: true,
          },
        ],
        values: {
          status: Object.values(observationStatus).map((s) => ({
            value: s.key,
            title: s.title,
          })),
          type: observationClassificationTypes.map((t) => ({
            title: t,
            value: t,
          })),
          negative: [
            { title: keys.observations.POSITIVE, value: 'false' },
            { title: keys.observations.ACTION_REQUIRED, value: 'true' },
          ],
          creatorId: userValues,
          participants: userValues,
        },
        pivotColumns: [
          ...Object.values(observationStatus).map((s) => ({
            value: s.key,
            title: s.title,
            fieldString: keys.common.STATUS,
            field: 'status',
          })),
          ...observationClassificationTypes.map((t) => ({
            title: t,
            value: t,
            fieldString: keys.common.TYPE,
            field: 'type',
          })),
        ],
      },
      audit: {
        value: 'audits',
        refModel: 'inspection',
        label: `${t(keys.common.AUDIT)}s`,
        show: auditsAndCorrectiveActions,
        icon: 'checklist',
        group: 'creatorId',
        groups: [creatorId, auditType, status, vehicleId, passed, participants],
        columns: [
          c.isDraft,
          c.passed,
          c.templateTitle,
          c.subject,
          c.workspaceId,
          c.vehicleId,
          c.participants,
          c.location,
          c.labels,
          c.private,
          ...baseTypes,
        ],
        onClick: ({ original }) => {
          const ws =
            original?.workspaceId ||
            original?.workspace?.id ||
            providerWorkspaceId;
          const path = getRoute(
            ws,
            paths.assessment,
            original.id,
            `?type=${original?.inspectionType}`,
          );
          const url = new URL(path, window.location.origin).toString();
          window.open(url, '_blank', 'noopener,noreferrer');
        },
        filters: [
          {
            field: 'inspectionType',
            operator: 'eq',
            value: ['AUDIT'],
            default: true,
          },
        ],
        fields: [
          { title: keys.common.TYPE, value: 'auditType' },
          { title: keys.common.STATUS, value: 'status' },
          { title: keys.common.ASSET, value: 'vehicleId' },
          { title: 'Checklist', value: 'templateId' },
          {
            title: keys.common.LABELS,
            value: 'labels',
            subfilter: true,
          },
          { title: keys.common.CREATOR, value: 'creatorId' },
          {
            title: keys.common.PARTICIPANTS,
            value: 'participants',
            subfilter: true,
          },
        ],
        values: {
          auditType: auditTypes.map((t) => ({ title: t, value: t })),
          status: [
            ...Object.entries(assessmentStatus)
              .filter(
                ([key]) =>
                  !key.includes('CHANGE') &&
                  !key.includes('REVIEW') &&
                  !key.includes('ACTION'),
              )
              .map(([key, value]) => ({
                value: key,
                title: value?.title,
                field: 'status',
                fieldString: keys.common.STATUS,
              })),
          ],
          // allTemplates, not activeTemplates — see the same field above for
          // why
          templateId: [
            ...allTemplates
              .filter((t) => t.templateType === 'AUDIT')
              .map((t) => {
                return { ...t, value: t?.id };
              }),
          ],
          vehicleId: [
            ...allVehicles.map((v) => {
              return { ...t, value: v.id, title: vehicleString(v) };
            }),
          ],
          labels: allLabels?.map((l) => ({
            ...l,
            title: l?.name,
            value: l?.id,
          })),
          creatorId: userValues,
          participants: userValues,
        },
        pivotColumns: [
          ...auditTypes.map((t) => ({
            title: t,
            value: t,
            fieldString: keys.common.TYPE,
            field: 'audit_type',
          })),
          ...allVehicles.map((v) => {
            return {
              value: v.id,
              title: vehicleString(v),
              fieldString: keys.common.ASSET,
              field: 'vehicle_id',
              img: v?.imageUrl,
            };
          }),
          ...activeTemplates
            .filter((t) => t.templateType === 'AUDIT')
            .map((t) => {
              return {
                title: t?.title,
                value: t?.id,
                fieldString: keys.common.TEMPLATE,
                field: 'template_id',
                icon: assessmentTypes[t?.templateType.toLowerCase()]?.icon,
                iconProps: {
                  color:
                    customColors[
                      assessmentTypes[
                        t?.templateType.toLowerCase()
                      ]?.color.toUpperCase()
                    ],
                },
              };
            }),
          ...Object.entries(assessmentStatus)
            .filter(
              ([key]) =>
                !key.includes('CHANGE') &&
                !key.includes('REVIEW') &&
                !key.includes('ACTION'),
            )
            .map(([key, value]) => ({
              value: key,
              title: value?.title,
              field: 'status',
              fieldString: keys.common.STATUS,
            })),
        ],
      },
      correctiveAction: {
        value: 'correctiveActions',
        label: `${t(keys.common.CORRECTIVE_ACTION)}s`,
        show: auditsAndCorrectiveActions,
        icon: 'error_outline',
        groups: [creatorId, status, type, vehicleId, assigneeId, riskLevel],
        columns: [
          c.cpaStatus,
          c.riskLevel,
          c.cpaExtensionRequested,
          c.narrative,
          c.vehicleId,
          c.assigneeId,
          ...baseTypes,
        ],
        onClick: (row) => {
          const path = `${paths.cpa}/${row.original.id}`;
          const url = new URL(path, window.location.origin).toString();
          window.open(url, '_blank', 'noopener,noreferrer');
        },
        group: 'creatorId',
        filters: [],
        fields: [
          { title: keys.common.STATUS, value: 'status' },
          { title: keys.common.TYPE, value: 'type' },
          { title: 'Risk Level', value: 'riskLevel' },
          { title: 'OFI', value: 'isObservation' },
          { title: keys.common.CREATOR, value: 'creatorId' },
        ],
        values: {
          status: [
            { title: 'Interim', value: 'INTERIM' },
            { title: 'Final', value: 'FINAL' },
            { title: 'Acceptance', value: 'ACCEPTED' },
            { title: 'Reviewed', value: 'REVIEWED' },
            { title: 'Denied', value: 'DENIED' },
            { title: 'Complete', value: 'COMPLETE' },
          ],
          type: [
            { title: 'Interal', value: 'Internal' },
            { title: 'External', value: 'External' },
            { title: 'Vendor', value: 'Vendor' },
          ],
          riskLevel: [
            { title: 'High', value: 'HIGH' },
            { title: 'Medium', value: 'MEDIUM' },
            { title: 'Low', value: 'LOW' },
            { title: 'Observation', value: 'OBSERVATION' },
          ],
          isObservation: [
            { title: 'Is Not Observation', value: 'false' },
            { title: 'Is Observation', value: 'true' },
          ],
          creatorId: userValues,
        },
        pivotColumns: [
          {
            title: 'Interim',
            value: 'INTERIM',
            fieldString: keys.common.STATUS,
            field: 'status',
          },
          {
            title: 'Final',
            value: 'FINAL',
            fieldString: keys.common.STATUS,
            field: 'status',
          },
          {
            title: 'Acceptance',
            value: 'ACCEPTED',
            fieldString: keys.common.STATUS,
            field: 'status',
          },
          {
            title: 'Reviewed',
            value: 'REVIEWED',
            fieldString: keys.common.STATUS,
            field: 'status',
          },
          {
            title: 'Denied',
            value: 'DENIED',
            fieldString: keys.common.STATUS,
            field: 'status',
          },
          {
            title: 'Complete',
            value: 'COMPLETE',
            fieldString: keys.common.STATUS,
            field: 'status',
          },
          {
            title: 'High',
            value: 'HIGH',
            fieldString: 'Risk Level',
            field: 'risk_level',
          },
          {
            title: 'Medium',
            value: 'MEDIUM',
            fieldString: 'Risk Level',
            field: 'risk_level',
          },
          {
            title: 'Low',
            value: 'LOW',
            fieldString: 'Risk Level',
            field: 'risk_level',
          },
          {
            title: 'Observation',
            value: 'OBSERVATION',
            fieldString: 'Risk Level',
            field: 'risk_level',
          },
          {
            title: 'Interal',
            value: 'Internal',
            fieldString: keys.common.TYPE,
            field: 'type',
          },
          {
            title: 'External',
            value: 'External',
            fieldString: keys.common.TYPE,
            field: 'type',
          },
          {
            title: 'Vendor',
            value: 'Vendor',
            fieldString: keys.common.TYPE,
            field: 'type',
          },
          ...Object.entries(rootCauseMap).map(([key, value]) => ({
            fieldString: 'Root Cause',
            field: 'root_cause',
            value: key,
            title: value,
          })),
        ],
      },
      timecard: {
        value: 'timecards',
        group: 'creatorId',
        label: t(keys.common.TIMECARDS),
        show: enableTimecards,
        icon: 'timer',
        groups: [workspaceId, creatorId, status],
        filters: [],
        fields: [
          { title: keys.common.STATUS, value: 'status' },
          { title: keys.common.CREATOR, value: 'creatorId' },
        ],
        values: {
          status: Object.entries(timecardStatusTypes).map(([key, value]) => ({
            value: key,
            title: value?.title,
          })),
          creatorId: userValues,
        },
        onClick: (row) => {
          const path = `${paths.timecard}/${row.original.id}`;
          const url = new URL(path, window.location.origin).toString();
          window.open(url, '_blank', 'noopener,noreferrer');
        },
        columns: [
          c.description,
          c.workspaceId,
          c.timecardStatus,
          c.vehicleId,
          c.billableHours,
          ...baseTypes,
        ],
      },
      expense: {
        value: 'expenses',
        group: 'creatorId',
        label: t(keys.common.EXPENSES),
        show: enableExpenses,
        icon: 'paid',
        groups: [workspaceId, creatorId, status],
        filters: [],
        fields: [
          { title: keys.common.STATUS, value: 'status' },
          { title: keys.common.CREATOR, value: 'creatorId' },
        ],
        values: {
          status: Object.entries(expenseStatusTypes).map(([key, value]) => ({
            value: key,
            title: value?.title,
          })),
          creatorId: userValues,
        },
        onClick: (row) => {
          const path = `${paths.expense}/${row.original.id}`;
          const url = new URL(path, window.location.origin).toString();
          window.open(url, '_blank', 'noopener,noreferrer');
        },
        columns: [
          c.expenseStatus,
          c.expenseType,
          c.total,
          c.description,
          c.workspaceId,
          ...baseTypes,
        ],
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      c.private,
      c.actionItemType,
      c.actionItemStatus,
      c.priority,
      c.assigneeId,
      c.participants,
      c.dueDate,
      c.workspaceId,
      c.title,
      c.description,
      c.workspace,
      c.templateTitle,
      c.subject,
      c.passed,
      c.labels,
      c.incidentStatus,
      status,
      type,
      vehicleId,
      priority,
      participants,
      assessmentName,
      isDraft,
      inspectionType,
      location,
      passed,
      incidentName,
      subtype,
      lostTime,
      nearMiss,
      observationName,
      dateTime,
      auditType,
      assigneeId,
      navigate,
      openActionItemModal,
      activeTemplates,
      company?.riskMatrix?.levels,
      userValues,
    ],
  );
  return (
    Object.fromEntries(
      Object.entries(modelOptions).filter(([, v]) => v.show),
    ) || []
  );
}

export function getEventDate(event) {
  const ms = Number(event && event.dateCreated);
  if (Number.isFinite(ms)) return new Date(ms);
  return new Date(event && event.dateCreated);
}
export function getEventGroupValue(event, group, model) {
  const groups = [
    'workspaceId',
    'assigneeId',
    'userId',
    'paricipants',
    'priority',
  ];
  if (!event) return null;
  if (group === 'participants') {
    return participantModelLookup(model);
  }
  if (groups.includes(group)) {
    return event[group] ?? null;
  }

  const info = event.information || {};
  const infoKey = groupLookUp(group);

  if (!infoKey) return null;
  if (info[infoKey] != null) return info[infoKey];
  const deltaVal = info.Delta && info.Delta[infoKey];
  if (deltaVal == null) return null;
  if (typeof deltaVal === 'object' && deltaVal.to != null) return deltaVal.to;
  return deltaVal;
}
export function buildLineSeries(events = [], group) {
  const byGroup = {};

  for (const e of events) {
    const d = getEventDate(e);
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) continue;

    const day = d.toISOString().slice(0, 10);

    const groupVal = getEventGroupValue(e, group);
    const seriesId = groupVal == null ? 'None' : String(groupVal);

    if (!byGroup[seriesId]) byGroup[seriesId] = {};
    byGroup[seriesId][day] = (byGroup[seriesId][day] || 0) + 1;
  }

  return Object.entries(byGroup).map(([id, dayMap]) => ({
    id,
    data: Object.entries(dayMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([x, y]) => ({ x, y })),
  }));
}
export const reportAttachmentTypes = {
  text: { key: 'TEXT', title: 'Text', icon: 'edit_notes' },
  graph: {
    key: 'GRAPH',
    type: ['PIE', 'LINE', 'BAR'],
    title: 'Graph',
    icon: 'add_chart',
  },
  data: { key: 'DATA', type: ['TABLE'], title: 'Data', icon: 'table' },
  // stats: {
  //   key: 'STATS',
  //   type: [],
  //   title: 'Stats',
  //   icon: 'query_stats',
  // },
};
export const graphTypes = [
  { key: 'PIE', icon: 'pie_chart_outlined' },
  { key: 'LINE', icon: 'timeline' },
  { key: 'BAR', icon: 'bar_chart' },
  {
    key: 'BAR_HORIZONTAL',
    icon: 'bar_chart',
    iconProps: { transform: 'rotate(90deg)' },
  },
  {
    key: 'PIVOT',
    icon: 'table_chart',
  },
];

export const dropdownTypes = {
  workspace: { placeholder: keys.common.WORKSPACE },
  workspace_not_null: { placeholder: keys.common.WORKSPACES },
  template: { placeholder: keys.common.TEMPLATE },
  asset: { placeholder: keys.common.ASSET },
  user: { placeholder: keys.common.USER },
};
const asDate = (d) => {
  if (d instanceof Date) return d;
  if (typeof d === 'string' || typeof d === 'number') {
    const parsed = new Date(d);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

export const buildReportOptions = ({
  daysBack = '30',
  key = 'dateCreated',
  model = 'actionItem',
  refModel = 'actionItem',
  to,
  from,
  workspaces = [],
  type = 'TEXT',
  group = 'workspaceId',
  subgroup = null,
  filters = [],
  where = {},
  description = '',
  total = 0,
  title = null,
  columns = null,
  riskRanges = null,
  timeRange,
  target,
} = {}) => {
  const today = new Date();
  const d = parseInt(daysBack) || 30;
  const end = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - 1,
    23,
    59,
    59,
    999,
  );
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - d,
    0,
    0,
    0,
    0,
  );
  const toDate = asDate(timeRange.to || to) ?? end;
  const fromDate = asDate(timeRange.from || from) ?? start;
  const value = `${model}s`;
  const targetNum =
    target === '' || target == null ? undefined : Number(target);
  return {
    model,
    refModel,
    group,
    timeRange: {
      to: toDate.toISOString(),
      from: fromDate.toISOString(),
      key,
      reoccur: '90',
    },
    // `workspaces` is an array of full objects ({id, title, ...}) when it
    // comes fresh off the workspace picker, but a plain array of ids when
    // it was loaded straight from a previously-saved attachment's content
    // (see ReportBuildMenu.jsx loading an existing attachment into
    // menuState) and the user re-saves without touching that picker.
    // `.id` on a raw id is undefined, which JSON.stringify then turns into
    // a `null` per entry — so this must handle both shapes.
    workspaces: workspaces
      ?.filter(Boolean)
      ?.map((w) => (typeof w === 'object' ? w.id : w)),
    filters,
    where,
    type,
    key,
    daysBack,
    open: false,
    description,
    total,
    value,
    subgroup,
    title,
    columns,
    riskRanges,
    target: Number.isFinite(targetNum) ? targetNum : undefined,
  };
};
export const GetReportSidebarOptions = (
  state,
  setState,
  readOnly,
  updateReport,
) => {
  const { addParticipant, deleteParticipant } = useParticipantMutations();
  const { t } = useTranslation();
  const options = [
    {
      title: t(keys.common.STATUS),
      editable: false,
      key: 'status',
      type: 'label',
      name:
        reportStatusTypes[state.status]?.title || reportStatusTypes.DRAFT.title,
      color:
        reportStatusTypes[state.status]?.color || reportStatusTypes.DRAFT.color,
    },
    {
      title: t(keys.common.CREATOR),
      user: state.creator,
      editable: false,
      key: 'creator',
      type: 'user',
    },
    {
      title: 'Recipients',
      selected: state?.participants || [],
      editable: false,
      key: 'participants',
      type: 'multiuser',
      showUsers: 'all',
      onUserAdded: (user) => {
        setState({
          ...state,
          participants: [...(state.participants || []), user],
        });
        addParticipant({
          userId: user.id,
          parentId: state.id,
          parentModel: 'report',
        });
      },
      onUserRemoved: (user) => {
        setState({
          ...state,
          participants: state?.participants?.filter((u) => u.id !== user.id),
        });
        deleteParticipant({
          userId: user.id,
          parentId: state?.id,
          parentModel: 'report',
        });
      },
      stateUpdateOnly: true,
      readOnly: state.status === 'COMPLETE',
      disableSelectAll: true,
    },
    {
      title: 'Report Interval',
      value: intervalOptions[state?.interval]?.label,
      selected: intervalOptions[state?.interval] || intervalOptions.WEEK,
      editable: false,
      key: 'interval',
      type: 'dropdown',
      required: true,
      options: Object.values(intervalOptions).map((v) => v),
      onChange: (interval) => {
        setState({ ...state, interval: interval.value });
        updateReport({
          variables: {
            reportId: state.id,
            interval: interval.value,
          },
        });
      },
    },
    {
      title: state.interval === 'ONCE' ? 'Send Report' : 'Start Date',
      value: getCalendarDate(state?.nextRunAt)?.isValid()
        ? getCalendarDate(state?.nextRunAt).format('YYYY-MM-DD')
        : state?.startDate ?? null,
      date: getCalendarDate(state?.nextRunAt)?.isValid()
        ? getCalendarDate(state?.nextRunAt).format('dddd MMMM Do, YYYY')
        : getCalendarDate(state?.startDate)?.isValid()
        ? getCalendarDate(state?.startDate).format('dddd MMMM Do, YYYY')
        : 'None',
      required: true,
      key: 'startDate',
      type: 'date',
      show: state?.status === 'DRAFT',
      min:
        state.interval === 'ONCE'
          ? moment().format('YYYY-MM-DD')
          : moment()?.add(1, 'day')?.format('YYYY-MM-DD'),
      onChange: (e) => {
        const value = e?.target?.value;
        setState({ ...state, startDate: !!value ? value : null });
        updateReport({
          variables: { reportId: state.id, startDate: !!value ? value : null },
        });
      },
    },
    {
      title: 'Next Report Scheduled',
      value: getCalendarDate(state?.nextRunAt)?.isValid()
        ? `${getCalendarDate(state?.nextRunAt).format('dddd MMMM Do, YYYY')}`
        : 'None',
      editable: false,
      key: 'nextRunAt',
      type: 'text',
      show: state?.status === 'PUBLISHED',
    },
    {
      title: 'Report Date',
      value: getCalendarDate(state?.dateCreated)?.isValid()
        ? `${getCalendarDate(state?.dateCreated).format('dddd MMMM Do, YYYY')}`
        : 'None',
      editable: false,
      key: 'dateCreated',
      type: 'text',
      show: state?.status === 'COMPLETE',
    },
  ];
  return options;
};

export const SidebarSwitches = (state, onEdit) => {
  return [
    {
      checked: state.emailPdf,
      label: state?.emailPdf ? (
        <span style={{ fontSize: '0.85rem' }}>
          PDF included in Email Notification
        </span>
      ) : (
        <span style={{ fontSize: '0.85rem' }}>
          PDF not included in Email Notification
        </span>
      ),
      onChange: onEdit,
      disabled: false,
      show: true,
      field: 'emailPdf',
      fontSize: 'sm',
    },
    {
      checked: state.emailXlsx,
      label: state?.emailXlsx ? (
        <span style={{ fontSize: '0.85rem' }}>
          XLSX file included in Email Notification
        </span>
      ) : (
        <span style={{ fontSize: '0.85rem' }}>
          XLSX file not included in Email Notification
        </span>
      ),
      onChange: onEdit,
      disabled: false,
      show: true,
      field: 'emailXlsx',
    },
  ];
};
export const SidebarButtons = (
  state,
  onEdit,
  onDelete,
  onPublish,
  onAdd,
  onPDF,
  disabled,
  onPause,
  onExcelExport,
) => {
  return [
    {
      value: 'Publish',
      icon: 'thumb_up_off_alt',
      variant: 'success',
      show: state.status === 'DRAFT',
      onClick: onPublish,
      outlined: false,
      disabled,
      id: 'download-report-pdf',
    },
    {
      value: 'Add Content',
      icon: 'edit',
      variant: 'orange',
      show: state.status === 'DRAFT',
      onClick: onAdd,
      outlined: true,
    },
    {
      value: state.status !== 'COMPLETE' ? 'Preview PDF' : 'Export PDF',
      icon: 'file_upload',
      variant: 'primary',
      show: true,
      onClick: onPDF,
      outlined: true,
    },
    {
      value: 'Export Excel',
      icon: 'table_view',
      variant: 'primary',
      show: !!onExcelExport,
      onClick: onExcelExport,
      outlined: true,
      disabled,
    },
    {
      value: 'Edit',
      icon: 'edit',
      variant: 'warning',
      onClick: onEdit,
      outlined: true,
      show: state.status === 'PUBLISHED',
    },
    {
      value: 'Pause',
      icon: 'pause_circle',
      variant: 'orange',
      onClick: () => onPause('PAUSED'),
      outlined: true,
      show: state.status === 'PUBLISHED',
    },
    {
      value: 'Resume',
      icon: 'play_circle',
      variant: 'success',
      onClick: () => onPause('PUBLISHED'),
      outlined: true,
      show: state.status === 'PAUSED',
    },
    {
      value: 'Delete',
      icon: 'delete',
      variant: 'danger',
      show: state.status !== 'COMPLETE',
      onClick: onDelete,
      outlined: true,
    },
  ];
};
export function useReportLabel(group, model) {
  const { allWorkspaces, allVehicles } = useWorkspace();
  const { allUsers } = useWorkspacePermissions();
  const { labels } = useLabels();
  const { t } = useTranslation();
  return useCallback(
    (raw) => {
      // Grouping by a date field (e.g. dateOfIncident) returns the raw
      // ISO timestamp per row — undo that to a readable, minute-precision
      // label rather than falling through to the default case's String(raw)
      // (full ISO string, milliseconds and all).
      if (DATE_COLUMN_KEYS.has(group)) {
        return raw
          ? getLocalTime(raw).format('MMM D, YYYY h:mm A')
          : t(keys.common.NONE);
      }
      switch (group) {
        case 'workspaceId': {
          const workspaceId = Number(raw);
          const workspace = allWorkspaces.find((w) => w.id === workspaceId);
          return workspace?.title
            ? workspace.title
            : workspace?.id
            ? `WorkspaceId${workspace.id}`
            : t(keys.common.SHARED);
        }
        case 'participants':
        case 'creatorId':
        case 'assigneeId':
        case 'userId': {
          const userId = Number(raw);
          const user = allUsers.find((u) => u.id === userId);
          return user?.id
            ? user.firstName || user.lastName
              ? `${user.firstName} ${user.lastName}`.trim()
              : `UserID${user.id}`
            : userId > 0
            ? `UserID: ${userId} (not active)`
            : group === 'creatorId'
            ? 'Anonymous'
            : 'None';
        }
        case 'riskLevel':
          return raw ? String(raw) : t(keys.common.NONE);
        case 'isDraft':
          return !raw ? t(keys.common.COMPLETED) : t(keys.common.IN_PROGRESS);
        case 'passed':
          return !raw ? t(keys.common.FAILED) : t(keys.common.PASSED);
        case 'nearMiss':
        case 'lostTime':
          return !raw === true ? t(keys.common.NO) : t(keys.common.YES);
        case 'vehicleId': {
          const vehicleId = Number(raw);
          const vehicle = allVehicles.find((v) => v.id === vehicleId);
          return vehicle?.id ? vehicleString(vehicle) : `No Asset`;
        }
        case 'labels':
          const labelId = Number(raw);
          const label = labels.find((l) => l.id === labelId);
          return label?.name ?? 'unknown';
        case 'status':
          if (model === 'timecard' || model === 'expense') {
            return t(getTranslationKey(String(raw), model + 's'));
          }
          return !!raw
            ? t(getTranslationKey(String(raw), 'common')) ||
                t(getTranslationKey(String(raw), 'incidents')) ||
                humanizeEnumBasic(String(raw)) ||
                String(raw)
            : t(keys.common.NONE);
        case 'templateId':
          return raw ? String(raw) : `Err: No Template Found`;
        default:
          return !!raw
            ? t(getTranslationKey(String(raw), 'common')) ||
                t(getTranslationKey(String(raw), 'incidents')) ||
                humanizeEnumBasic(String(raw)) ||
                String(raw)
            : t(keys.common.NONE);
      }
    },
    [group, allWorkspaces, allVehicles, allUsers, t, labels, model],
  );
}
export async function waitForSvg(root, { tries = 12, delay = 100 } = {}) {
  for (let i = 0; i < tries; i++) {
    const svg = root?.querySelector?.('svg');
    if (svg) return svg;
    await new Promise((r) => setTimeout(r, delay));
  }
  return null;
}

export async function svgToPngBlob(
  svgEl,
  { scale = 2, background = '#fff' } = {},
) {
  if (!svgEl) throw new Error('No SVG element');

  const svg = svgEl.cloneNode(true);
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

  const bbox = svgEl.getBoundingClientRect?.() || { width: 800, height: 400 };
  const w = Number(svgEl.getAttribute('width')) || bbox.width || 800;
  const h = Number(svgEl.getAttribute('height')) || bbox.height || 400;

  const xml = new XMLSerializer().serializeToString(svg);
  const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise((res, rej) => {
    img.onload = res;
    img.onerror = rej;
    img.src = url;
  });

  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(w * scale);
  canvas.height = Math.ceil(h * scale);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  URL.revokeObjectURL(url);

  return new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob), 'image/png', 1),
  );
}

export function chartTitle(a, models, type, timeSetting = 'standard') {
  return buildChartTitle(a, models, {
    t: i18n.t,
    getTime: getLocalTime,
    timeSetting,
  });
}

export async function exportPng(props) {
  const { ref, to, from, model, group } = props || {};
  let container = ref?.current;
  if (!container) return null;
  const svg = await waitForSvg(container);
  if (!svg) return null;
  const blob = await svgToPngBlob(svg, { scale: 2, background: '#fff' });
  const safe = (s) => String(s ?? '').replace(/[^\w.-]+/g, '_');
  const start = (from || '').slice(0, 10);
  const end = (to || '').slice(0, 10);
  const filename = `${safe(model)}_${safe(group)}_${safe(start)}--${safe(
    end,
  )}.png`;

  const file = new File([blob], filename, { type: 'image/png' });

  return file;
}
export const reportDateOptions = [
  { value: '7', label: '7' },
  { value: '14', label: '14' },
  { value: '30', label: '30' },
  { value: '90', label: '90' },
  { value: '365', label: '365' },
  { value: 'ytd', label: 'YTD' },
  { value: 'monthly', label: 'Month' },
  { value: 'yearly', label: 'Year' },
  { value: 'custom_days', label: 'Custom' },
  {
    value: 'custom',
    label: 'Range',
  },
  { value: 'from', label: 'From' },
];

function getExportMatrix(table) {
  const cols = table.getVisibleLeafColumns();
  if (!cols.length) return { headers: [], rows: [] };

  const flatRows = table.getPrePaginationRowModel().flatRows ?? [];
  const leafRows = flatRows.filter((r) => !r.subRows?.length);

  const headers = cols.map((c) => {
    const h = c.columnDef?.header;
    return typeof h === 'string' ? h : c.id;
  });

  const rows = leafRows.map((r) =>
    cols.map((c) => {
      let v = r.getValue(c.id);
      if (v == null) return '';
      if (v instanceof Date) return v.toISOString();
      if (v?._isAMomentObject)
        return v.toISOString?.() ?? v.format?.() ?? String(v);
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v);
    }),
  );

  return { headers, rows };
}

export async function exportXLSX(table, filename = 'report.xlsx') {
  const { headers, rows } = getExportMatrix(table);
  if (!headers.length) return;

  const mod = await import('xlsx');
  const XLSX = mod.default || mod;

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, filename);
}

export { formatPivotDataForExcel } from '@opas-mobile/report-export';
export { toDailySeries } from '@opas-mobile/report-export';
